import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Building, Upload, Loader2, Save, ImageOff } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";

const IT_FIELDS = [
  { key: "studio_nome", label: "Ragione sociale", id: "name", placeholder: "Studio Rossi & Associati" },
  { key: "indirizzo", label: "Indirizzo", id: "address", placeholder: "Via Roma 1, 20100 Milano" },
  { key: "piva", label: "P.IVA", id: "piva" },
  { key: "email", label: "Email", id: "email" },
  { key: "telefono", label: "Telefono", id: "phone" },
];

const UK_FIELDS = [
  { key: "uk_nome", label: "Company name", id: "name", placeholder: "Studio Rossi Ltd" },
  { key: "uk_indirizzo", label: "Address", id: "address", placeholder: "1 King Street, London EC2V 8AU" },
  { key: "uk_vat", label: "VAT number", id: "vat" },
  { key: "uk_email", label: "Email", id: "email" },
  { key: "uk_telefono", label: "Phone", id: "phone" },
];

const OfficeFields = ({ title, testPrefix, form, setForm, fields }) => (
  <div className="rounded-md border p-4 space-y-3" data-testid={`${testPrefix}-office-section`}>
    <h3 className="text-sm font-semibold uppercase tracking-wide text-primary">{title}</h3>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {fields.map((f) => (
        <div key={f.key} className={`space-y-1.5 ${f.id === "name" || f.id === "address" ? "sm:col-span-2" : ""}`}>
          <Label htmlFor={`${testPrefix}-${f.id}`}>{f.label}</Label>
          <Input id={`${testPrefix}-${f.id}`} value={form[f.key] || ""} placeholder={f.placeholder}
            onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} data-testid={`${testPrefix}-${f.id}-input`} />
        </div>
      ))}
    </div>
  </div>
);


export default function Settings() {
  const [form, setForm] = useState({ studio_nome: "", indirizzo: "", piva: "", email: "", telefono: "", uk_nome: "", uk_indirizzo: "", uk_vat: "", uk_email: "", uk_telefono: "", logo_base64: "", cover_logo_base64: "", report_footer_text: "", report_footer_text_en: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setForm((f) => ({ ...f, ...r.data }))).finally(() => setLoading(false));
  }, []);

  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1.5 * 1024 * 1024) {
      toast.error("Logo troppo grande (max 1.5MB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, logo_base64: reader.result }));
    reader.readAsDataURL(file);
  };

  const onCoverLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1.5 * 1024 * 1024) {
      toast.error("Logo troppo grande (max 1.5MB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, cover_logo_base64: reader.result }));
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", form);
      toast.success("Impostazioni studio salvate");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Layout><div className="flex items-center justify-center py-20 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin mr-2" /> Caricamento...</div></Layout>;

  return (
    <Layout>
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Configurazione</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Intestazione Studio</h1>
        <p className="text-sm text-muted-foreground mt-2">Logo e dati che compaiono in cima ai report PDF ed Excel esportati.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-6 lg:col-span-1">
          <Label className="mb-3 block">Logo Studio</Label>
          <div className="aspect-square rounded-lg border border-dashed border-border grid place-items-center bg-secondary/30 overflow-hidden mb-4">
            {form.logo_base64 ? (
              <img src={form.logo_base64} alt="Logo" className="max-h-full max-w-full object-contain" data-testid="logo-preview" />
            ) : (
              <div className="text-center text-muted-foreground"><ImageOff className="h-8 w-8 mx-auto mb-2" /><span className="text-xs">Nessun logo</span></div>
            )}
          </div>
          <input id="logo" type="file" accept="image/*" className="hidden" onChange={onLogo} data-testid="logo-input" />
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={() => document.getElementById("logo").click()} data-testid="logo-upload-btn">
              <Upload className="h-4 w-4 mr-2" /> Carica
            </Button>
            {form.logo_base64 && (
              <Button variant="ghost" onClick={() => setForm({ ...form, logo_base64: "" })} data-testid="logo-remove-btn">Rimuovi</Button>
            )}
          </div>

          <Label className="mb-3 mt-6 block">Logo Copertina (opzionale)</Label>
          <p className="text-xs text-muted-foreground mb-3">Se presente, usato nella copertina del PDF al posto del logo studio.</p>
          <div className="aspect-square rounded-lg border border-dashed border-border grid place-items-center bg-secondary/30 overflow-hidden mb-4">
            {form.cover_logo_base64 ? (
              <img src={form.cover_logo_base64} alt="Logo copertina" className="max-h-full max-w-full object-contain" data-testid="cover-logo-preview" />
            ) : (
              <div className="text-center text-muted-foreground"><ImageOff className="h-8 w-8 mx-auto mb-2" /><span className="text-xs">Nessun logo copertina</span></div>
            )}
          </div>
          <input id="coverlogo" type="file" accept="image/*" className="hidden" onChange={onCoverLogo} data-testid="cover-logo-input" />
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={() => document.getElementById("coverlogo").click()} data-testid="cover-logo-upload-btn">
              <Upload className="h-4 w-4 mr-2" /> Carica
            </Button>
            {form.cover_logo_base64 && (
              <Button variant="ghost" onClick={() => setForm({ ...form, cover_logo_base64: "" })} data-testid="cover-logo-remove-btn">Rimuovi</Button>
            )}
          </div>
        </Card>

        <Card className="p-6 lg:col-span-2 space-y-4">
          <OfficeFields title="Sede Italia" testPrefix="studio" form={form} setForm={setForm} fields={IT_FIELDS} />
          <OfficeFields title="UK Office" testPrefix="uk" form={form} setForm={setForm} fields={UK_FIELDS} />
          <div className="space-y-2">
            <Label htmlFor="footer">Testo piè di pagina report (PDF)</Label>
            <Textarea id="footer" rows={2} value={form.report_footer_text || ""} onChange={(e) => setForm({ ...form, report_footer_text: e.target.value })} placeholder="Riservato - Preparato da : Bruno Benvenuti - TrustMaster S.r.l. - Documento per presentazione bancaria" data-testid="report-footer-input" />
            <p className="text-xs text-muted-foreground">Mostrato centrato in fondo a ogni pagina del PDF. Se vuoto, viene usato il testo predefinito.</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="footer-en">Testo piè di pagina report in inglese</Label>
            <Textarea id="footer-en" rows={2} value={form.report_footer_text_en || ""} onChange={(e) => setForm({ ...form, report_footer_text_en: e.target.value })} placeholder="Confidential - Prepared by: Bruno Benvenuti - TrustMaster S.r.l. - Document for bank presentation" data-testid="report-footer-en-input" />
            <p className="text-xs text-muted-foreground">Usato quando il report viene esportato in inglese.</p>
          </div>
          <div className="pt-2">
            <Button onClick={save} disabled={saving} data-testid="save-settings-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Salva Impostazioni
            </Button>
          </div>
        </Card>
      </div>
    </Layout>
  );
}
